import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";

import { Autoplay } from "swiper/modules";
import SwiperCore from "swiper";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import AllResults from "./SearchTabs/AllResults";
import Contractors from "./SearchTabs/Contractors";
import Contracts from "./SearchTabs/Contracts";
import LTAs from "./SearchTabs/LTAs";
import Assessments from "./SearchTabs/Assessments";
import {
  ICON_ASSESSMENTS,
  ICON_CONTRACTORS,
  ICON_CONTRACTS,
  ICON_LTAS,
  ICON_PEOPLE,
  ICON_SANCTIONS,
} from "common/constants";
import SanctionsList from "./SearchTabs/SantionsList";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { setSearchValue } from "slices/search/reducer";
import { useSearchParams, useLocation } from "react-router-dom";
import DownloadSearchReport from "./DownloadSearchReport";
import BulkSearch from "./BulkSearch";
import AutomatedSearch from "./AutomatedSearch";
import Persons from "./SearchTabs/Persons";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import Permission from "Components/Common/Permission";

type SearchMode = "search" | "bulk" | "automated";

const getSearchMode = (mode: string | null): SearchMode => {
  if (mode === "bulk" || mode === "automated") {
    return mode;
  }

  return "search";
};

const SearchResults = () => {
  const location = useLocation();
  usePageActivity(location.pathname, "Due Diligence Search");

  SwiperCore.use([Autoplay]);

  const dispatch: any = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchMode = getSearchMode(searchParams.get("mode"));
  const toggleSearchMode = (mode: SearchMode) => {
    if (searchMode === mode) {
      return;
    }

    setSearchParams((params) => {
      const newParams = new URLSearchParams(params);

      if (mode === "search") {
        newParams.delete("mode");
      } else {
        newParams.set("mode", mode);
      }

      return newParams;
    });
  };

  //Tab
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "1");
  const toggleTab = (tab: React.SetStateAction<string>) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setSearchParams(
        (params) => {
          const newParams = new URLSearchParams(params);
          newParams.set("tab", tab?.toString());
          return newParams;
        },
        { replace: true }
      );
    }
  };
  useEffect(() => {
    if (activeTab !== searchParams.get("tab")) {
      setActiveTab(searchParams.get("tab") || "1");
    }
  }, [activeTab, searchParams]);

  const selectProperties = createSelector(
    (state: any) => state.Search,
    (state) => ({
      globalSearchValue: state.globalSearchValue,
    })
  );
  const { globalSearchValue: searchValue } = useSelector(selectProperties);

  useEffect(() => {
    if (searchValue === "") {
      dispatch(setSearchValue(searchParams.get("search") || ""));
    }
  }, [dispatch, searchParams, searchValue]);

  const clearSearch = () => {
    if (searchValue.length > 0) {
      dispatch(setSearchValue(""));
      setSearchParams(
        (params) => {
          const newParams = new URLSearchParams(params);
          newParams.delete("search");
          return newParams;
        },
        { replace: true }
      );
    }
  };

  document.title = "Due Diligence Search | CIMS";

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb
            title="Due Diligence Search"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />
          <Card>
            <CardBody>
              <div className="d-lg-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="card-title mb-1">Due Diligence Search</h5>
                  <p className="mb-0 text-muted">
                    Search and screen organizations and individuals across CIMS
                    records and sanctions data.
                  </p>
                </div>
                <div className="flex-shrink-0 mt-4 mt-lg-0">
                  <ul
                    className="nav nav-pills filter-btns due-diligence-main-tabs"
                    role="tablist"
                  >
                    <Permission resource="search" action="proxySearch">
                      <li className="nav-item" role="presentation">
                        <button
                          type="button"
                          className={classnames("nav-link fw-medium", {
                            active: searchMode === "search",
                          })}
                          onClick={() => toggleSearchMode("search")}
                        >
                          <i className="ri-search-line align-bottom me-1"></i>
                          Search
                        </button>
                      </li>
                    </Permission>
                    <Permission resource="search" action="processBulkSearch">
                      <li className="nav-item" role="presentation">
                        <button
                          type="button"
                          className={classnames("nav-link fw-medium", {
                            active: searchMode === "bulk",
                          })}
                          onClick={() => toggleSearchMode("bulk")}
                        >
                          <i className="mdi mdi-table-search align-bottom me-1"></i>
                          Bulk Search
                        </button>
                      </li>
                    </Permission>
                    <Permission
                      resource="due-diligence-report"
                      action="startSearch"
                    >
                      <li className="nav-item" role="presentation">
                        <button
                          type="button"
                          className={classnames("nav-link fw-medium", {
                            active: searchMode === "automated",
                          })}
                          onClick={() => toggleSearchMode("automated")}
                        >
                          <i className="ri-sparkling-line align-bottom me-1"></i>
                          Automated Search
                        </button>
                      </li>
                    </Permission>
                  </ul>
                </div>
              </div>
            </CardBody>
          </Card>

          <TabContent activeTab={searchMode}>
            <TabPane tabId="search">
              <Card>
                <CardBody className="p-4">
                  <div className="position-relative mb-4">
                    <Input
                      type="text"
                      className="form-control form-control-lg bg-light border-light"
                      style={{
                        paddingRight: searchValue?.length > 0 ? "240px" : "190px",
                      }}
                      placeholder="Search here..."
                      value={searchValue}
                      onChange={(e) => {
                        dispatch(setSearchValue(e.target.value));

                        setSearchParams(
                          (params) => {
                            const newParams = new URLSearchParams(params);
                            newParams.set("search", e.target.value);
                            return newParams;
                          },
                          { replace: true }
                        );
                      }}
                    />

                    <div className="position-absolute end-0 top-0 bottom-0 d-flex align-items-center">
                      {searchValue?.length > 0 && (
                        <Button
                          color="link"
                          className="btn-lg link-muted px-2"
                          onClick={clearSearch}
                          title="Clear search"
                        >
                          <i className="mdi mdi-close-circle fs-18 text-muted"></i>
                        </Button>
                      )}
                      <div className="d-flex align-items-center border-start border-light">
                        <DownloadSearchReport />
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <Nav
                      className="nav-tabs nav-tabs-custom flex-grow-1"
                      role="tablist"
                    >
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classnames({
                            active: activeTab === "1",
                            "py-3": true,
                          })}
                          onClick={() => {
                            toggleTab("1");
                          }}
                        >
                          <i className="ri-search-2-line text-muted align-bottom me-1"></i>{" "}
                          All Results
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classnames({
                            active: activeTab === "2",
                            "py-3": true,
                          })}
                          onClick={() => {
                            toggleTab("2");
                          }}
                        >
                          <i
                            className={`${ICON_CONTRACTORS} text-muted align-bottom me-1`}
                          ></i>
                          Contractors
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classnames({
                            active: activeTab === "3",
                            "py-3": true,
                          })}
                          onClick={() => {
                            toggleTab("3");
                          }}
                        >
                          <i
                            className={`${ICON_CONTRACTS} text-muted align-bottom me-1`}
                          ></i>
                          Contracts
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classnames({
                            active: activeTab === "4",
                            "py-3": true,
                          })}
                          onClick={() => {
                            toggleTab("4");
                          }}
                        >
                          <i
                            className={`${ICON_LTAS} text-muted align-bottom me-1`}
                          ></i>{" "}
                          LTAs
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classnames({
                            active: activeTab === "5",
                            "py-3": true,
                          })}
                          onClick={() => {
                            toggleTab("5");
                          }}
                        >
                          <i
                            className={`${ICON_ASSESSMENTS} text-muted align-bottom me-1`}
                          ></i>{" "}
                          Assessments
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classnames({
                            active: activeTab === "6",
                            "py-3": true,
                          })}
                          onClick={() => {
                            toggleTab("6");
                          }}
                        >
                          <i
                            className={`${ICON_PEOPLE} text-muted align-bottom me-1`}
                          ></i>{" "}
                          Persons
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classnames({
                            active: activeTab === "7",
                            "py-3": true,
                          })}
                          onClick={() => {
                            toggleTab("7");
                          }}
                        >
                          <i
                            className={`${ICON_SANCTIONS} text-muted align-bottom me-1`}
                          ></i>{" "}
                          Sanctions
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>

                  <TabContent activeTab={activeTab} className="text-muted">
                    <TabPane tabId="1">
                      <AllResults
                        searchValue={searchValue}
                        activeTab={activeTab}
                      />
                    </TabPane>
                    <TabPane tabId="2">
                      <Contractors
                        searchValue={searchValue}
                        activeTab={activeTab}
                      />
                    </TabPane>
                    <TabPane tabId="3">
                      <Contracts
                        searchValue={searchValue}
                        activeTab={activeTab}
                      />
                    </TabPane>
                    <TabPane tabId="4">
                      <LTAs searchValue={searchValue} activeTab={activeTab} />
                    </TabPane>
                    <TabPane tabId="5">
                      <Assessments
                        searchValue={searchValue}
                        activeTab={activeTab}
                      />
                    </TabPane>
                    <TabPane tabId="6">
                      <Persons
                        searchValue={searchValue}
                        activeTab={activeTab}
                      />
                    </TabPane>
                    <TabPane tabId="7">
                      <SanctionsList
                        searchValue={searchValue}
                        activeTab={activeTab}
                      />
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </TabPane>

            <TabPane tabId="bulk">
              <BulkSearch />
            </TabPane>

            <TabPane tabId="automated">
              {searchMode === "automated" && <AutomatedSearch />}
            </TabPane>
          </TabContent>
        </div>
      </div>
    </React.Fragment>
  );
};

export default SearchResults;
